// ###### IMPORTS
import React, { useState, useEffect, useMemo } from 'react';
import { FixedSizeList as List } from 'react-window';
import { FunnelIcon } from '@heroicons/react/24/solid';
import { useAssetsData } from '../../../moduleServices/assets_data_context';
import DropdownOrderBy from './DropdownOrderBy';
import OperationCard from './OperationCard';
import ModalOperationEdit from '../Common/ModalOperationEdit';

const CARD_BODY_MAX_H = 'max-h-[50vh]';
const ITEM_SIZE = 64;
const LIST_HEIGHT_DEFAULT = 320;

// ###### SUBCOMPONENTS
const LoadingSkeleton = ({ rows = 5 }) => (
  <div className="p-3 space-y-2">
    {Array.from({ length: rows }).map((_, i) => (
      <div key={i} className="animate-pulse h-7 bg-gray-100 rounded" />
    ))}
  </div>
);

const EmptyState = () => (
  <div className="flex items-center justify-center py-8 text-gray-400 text-sm">
    No operations recorded.
  </div>
);

// ###### MAIN COMPONENT
const CardOperationsDetailed = ({ operationsUpdateRef }) => {
  const { operations, loading, error, refreshFull } = useAssetsData();
  const [isFilterExpanded, setIsFilterExpanded] = useState(false);
  const [listHeight, setListHeight] = useState(LIST_HEIGHT_DEFAULT);
  const [orderBy, setOrderBy] = useState('desc');
  const [assetNameSearch, setAssetNameSearch] = useState('');
  const [operationToEdit, setOperationToEdit] = useState(null);

  useEffect(() => {
    if (operationsUpdateRef) {
      operationsUpdateRef.current = refreshFull;
    }
    return () => {
      if (operationsUpdateRef) operationsUpdateRef.current = null;
    };
  }, [operationsUpdateRef, refreshFull]);

  useEffect(() => {
    const updateHeight = () => {
      const h = Math.max(120, Math.min(window.innerHeight * 0.5, 480));
      setListHeight(h);
    };
    updateHeight();
    window.addEventListener('resize', updateHeight);
    return () => window.removeEventListener('resize', updateHeight);
  }, []);

  const sortedOperations = useMemo(() => {
    const list = [...operations];
    const key = 'operation_date';
    list.sort((a, b) => {
      const da = a?.[key] || '';
      const db = b?.[key] || '';
      if (orderBy === 'asc') return da.localeCompare(db);
      return db.localeCompare(da);
    });
    return list;
  }, [operations, orderBy]);

  const filteredOperations = useMemo(() => {
    const term = (assetNameSearch?.trim() ?? '').toLowerCase();
    if (!term) return sortedOperations;
    return sortedOperations.filter((op) => {
      const name = (op?.asset_name ?? '').toLowerCase();
      const code = (op?.asset_code ?? '').toLowerCase();
      return name.includes(term) || code.includes(term);
    });
  }, [sortedOperations, assetNameSearch]);

  const Row = ({ index, style }) => {
    const operation = filteredOperations[index];
    if (!operation) return <div style={style} className="px-3 py-2 border-b border-gray-100 animate-pulse bg-gray-50 rounded" />;
    return <OperationCard operation={operation} style={style} onEditOperation={setOperationToEdit} />;
  };

  return (
    <>
    <div className="bg-white shadow rounded-xl overflow-hidden w-full">
      <div className="bg-gray-600 border-b border-gray-200 rounded-t-xl">
        <div className="px-4 py-2 flex justify-between items-center">
          <div className="flex items-center gap-2 text-white">
            <h2 className="text-lg font-semibold text-white">Operations</h2>
            {!loading && <span className="text-xs text-white opacity-70">({filteredOperations.length}{assetNameSearch?.trim() ? ` / ${operations.length}` : ''})</span>}
          </div>
          <div className="flex items-center gap-2">
            <div onClick={(e) => e.stopPropagation()}>
              <DropdownOrderBy value={orderBy} onChange={setOrderBy} />
            </div>
            <button
              type="button"
              onClick={() => setIsFilterExpanded((f) => !f)}
              className={`p-1 rounded-full transition-all ${
                isFilterExpanded || (assetNameSearch?.trim() ?? '') !== ''
                  ? 'bg-green-400 text-white'
                  : 'bg-gray-500 bg-opacity-30 text-white text-opacity-70 hover:bg-opacity-40'
              }`}
              aria-label="Toggle filters"
            >
              <FunnelIcon className="h-4 w-4" />
            </button>
          </div>
        </div>
        <div className={`overflow-hidden transition-all duration-200 ease-in-out ${isFilterExpanded ? 'h-10' : 'h-0'}`}>
          <div className="h-10 px-4 flex items-center bg-gray-500 bg-opacity-20 gap-4 flex-wrap">
            <input
              type="search"
              value={assetNameSearch}
              onChange={(e) => setAssetNameSearch(e.target.value)}
              placeholder="Nome do asset"
              className="h-6 px-2 rounded text-xs text-gray-800 bg-white border border-gray-300 min-w-[120px] max-w-[180px] placeholder:text-gray-400"
              aria-label="Filtrar por nome do asset"
            />
          </div>
        </div>
      </div>

      {error && (
        <div className="px-3 py-2 bg-red-50 border-b border-red-100 text-red-700 text-sm">
          {error}
        </div>
      )}
      <div className={CARD_BODY_MAX_H}>
        {loading && <LoadingSkeleton rows={5} />}
        {!loading && operations.length === 0 && <EmptyState />}
        {!loading && filteredOperations.length > 0 && (
          <List
            height={listHeight}
            itemCount={filteredOperations.length}
            itemSize={ITEM_SIZE}
            width="100%"
            overscanCount={5}
          >
            {Row}
          </List>
        )}
        {!loading && operations.length > 0 && filteredOperations.length === 0 && (
          <div className="flex items-center justify-center py-8 text-gray-400 text-sm">
            Nenhuma operação corresponde ao filtro.
          </div>
        )}
      </div>
    </div>
    {operationToEdit && (
      <ModalOperationEdit
        selectedOperation={operationToEdit}
        onClose={() => setOperationToEdit(null)}
        onSuccess={() => { setOperationToEdit(null); refreshFull(); }}
      />
    )}
    </>
  );
};

export default CardOperationsDetailed;
